// src/Dashboard/admin/components/miniCharts.jsx
import React from "react";

export function DonutChart({
  value = 0, // 0..100
  label,
  sublabel,
  size = 92,
  tone = "orange", // orange | green | slate
}) {
  const pct = clamp0_100(Number(value || 0));

  const toneRing =
    tone === "green"
      ? "from-green-500 to-emerald-400"
      : tone === "slate"
      ? "from-slate-700 to-slate-500"
      : "from-orange-600 to-amber-500";

  // conic-gradient for ring
  const style = {
    width: size,
    height: size,
    background: `conic-gradient(var(--ring) ${pct}%, rgba(148,163,184,0.25) 0)`,
  };

  return (
    <div className="flex items-center gap-3 min-w-0">
      <div
        className="rounded-full p-[2px] shrink-0"
        style={{
          backgroundImage: `linear-gradient(135deg, var(--ringA), var(--ringB))`,
          width: size,
          height: size,
        }}
      >
        <div
          className="rounded-full flex items-center justify-center bg-white dark:bg-slate-900"
          style={style}
        >
          <div
            className="rounded-full bg-white dark:bg-slate-900 flex items-center justify-center"
            style={{
              width: Math.round(size * 0.66),
              height: Math.round(size * 0.66),
            }}
          >
            <div className="text-center">
              <div className="text-base font-semibold text-slate-900 dark:text-slate-100">
                {pct}%
              </div>
              <div className="text-[10px] text-slate-500 -mt-0.5">rate</div>
            </div>
          </div>
        </div>
      </div>

      <div className="min-w-0">
        <div className="text-sm font-semibold truncate">{label}</div>
        {sublabel ? <div className="text-xs text-slate-500 truncate">{sublabel}</div> : null}
      </div>

      {/* CSS vars for ring gradient */}
      <style>{`
        :root {}
        .${toneRing.replace(/\s+/g, ".")} {}
      `}</style>

      <style>{`
        div[style*="--ringA"] {}
      `}</style>

      <style>{`
        /* provide variables based on tone without extra libs */
      `}</style>

      <Vars tone={tone} />
    </div>
  );
}

function Vars({ tone }) {
  const vars =
    tone === "green"
      ? { ring: "rgba(34,197,94,1)", ringA: "rgba(34,197,94,1)", ringB: "rgba(16,185,129,1)" }
      : tone === "slate"
      ? { ring: "rgba(51,65,85,1)", ringA: "rgba(51,65,85,1)", ringB: "rgba(100,116,139,1)" }
      : { ring: "rgba(234,88,12,1)", ringA: "rgba(234,88,12,1)", ringB: "rgba(245,158,11,1)" };

  return (
    <style>{`
      :root{
        --ring:${vars.ring};
        --ringA:${vars.ringA};
        --ringB:${vars.ringB};
      }
    `}</style>
  );
}

export function MiniBars({
  title,
  items = [], // [{ label, value }]
  max = null, // optional override
  suffix = "",
}) {
  const safe = Array.isArray(items) ? items.filter(Boolean) : [];
  const computedMax =
    max != null ? Number(max || 0) : Math.max(1, ...safe.map((x) => Number(x.value || 0)));

  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-4 bg-white dark:bg-slate-900 min-w-0">
      <div className="text-sm font-semibold mb-3 truncate">{title}</div>

      {safe.length === 0 ? (
        <div className="text-sm text-slate-500">No data yet.</div>
      ) : (
        <div className="space-y-2">
          {safe.slice(0, 6).map((x, idx) => {
            const v = Number(x.value || 0);
            const pct = computedMax > 0 ? Math.round((v / computedMax) * 100) : 0;
            return (
              <div key={idx} className="min-w-0">
                <div className="flex items-center justify-between gap-3 text-xs mb-1">
                  <span className="text-slate-600 dark:text-slate-300 truncate">{x.label}</span>
                  <span className="text-slate-500 shrink-0">
                    {fmtInt(v)}
                    {suffix}
                  </span>
                </div>
                <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                  <div
                    className="h-full bg-orange-600 dark:bg-orange-400"
                    style={{ width: `${clamp0_100(pct)}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function clamp0_100(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, Math.round(n)));
}

function fmtInt(v) {
  try {
    return new Intl.NumberFormat().format(Number(v || 0));
  } catch {
    return String(v ?? 0);
  }
}
